/**
 * 뷰포트.
 *
 * R3F는 여기서만 쓴다. 파이프라인은 three를 직접 다루고, React는 그 결과를
 * 씬에 꽂아 주는 역할만 한다. 지오메트리와 머티리얼을 React state로 만들려는
 * 유혹을 참으면 리렌더가 GPU 자원을 재생성하는 사고가 안 난다.
 */

import { useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { useForgeStore } from '../state/store.ts';
import { captureRef, forgedMeshRef, useViewStore } from '../state/view.ts';
import { useStudioEnvironment } from './environment.ts';
import { warmupSkinnedMesh } from '../core/rig/index.ts';
import { CLIP_META } from '../core/rig/animations.ts';

/** 메시가 아직 없을 때 셰이더를 스킨드 경로로 미리 컴파일시키는 티끌 메시. */
let warmup: THREE.SkinnedMesh | null = null;

function ForgedObject() {
  const mesh = useForgeStore((s) => s.mesh);
  const rig = useForgeStore((s) => s.rig);
  const clip = useForgeStore((s) => s.clip);
  const material = useForgeStore((s) => s.material);
  const wireframe = useViewStore((s) => s.wireframe);
  const autoRotate = useViewStore((s) => s.autoRotate);
  const group = useRef<THREE.Group>(null);
  const mixer = useRef<THREE.AnimationMixer | null>(null);

  if (!warmup) warmup = warmupSkinnedMesh(material.material);
  const object: THREE.Object3D = rig ? rig.skinned : warmup;

  useEffect(() => {
    forgedMeshRef.current = rig ? rig.skinned : null;
    // 디버그 훅 — 헤드리스 실측이 씬을 들여다본다(프로덕션 빌드에도 남긴다: 비용이 참조 하나다).
    (window as unknown as { __tf?: unknown }).__tf = { rig, scene: sceneRef.current };
    return () => { forgedMeshRef.current = null; };
  }, [rig]);

  // 4뷰 썸네일: 그룹을 돌려 가며 같은 렌더러로 찍는다. 새 컨텍스트를 만들면 셰이더를 또 컴파일한다(17초).
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const sceneRef = useRef<THREE.Scene | null>(null);
  sceneRef.current = scene;
  useEffect(() => {
    captureRef.current = (azimuths) => {
      const g = group.current;
      if (!g || !rig) return [];
      const prev = g.rotation.y;
      const out: string[] = [];
      for (const a of azimuths) {
        g.rotation.y = (-a * Math.PI) / 180;
        g.updateMatrixWorld(true);
        gl.render(scene, camera);
        out.push(gl.domElement.toDataURL('image/png'));
      }
      g.rotation.y = prev;
      return out;
    };
    return () => { captureRef.current = null; };
  }, [gl, scene, camera, rig]);

  // 새 이미지는 정면부터. 슬라이더로 메시만 다시 만들 때는 보던 각도를 유지한다.
  const fileName = useForgeStore((s) => s.fileName);
  useEffect(() => {
    if (group.current) group.current.rotation.set(0, 0, 0);
  }, [fileName]);

  const part = useForgeStore((s) => s.part);
  useEffect(() => {
    material.material.wireframe = wireframe;
    if (part) part.material.material.wireframe = wireframe;
  }, [material, wireframe, part]);

  // 믹서는 리그마다 하나. 클립이 바뀌면 크로스페이드.
  useEffect(() => {
    mixer.current?.stopAllAction();
    mixer.current = rig ? new THREE.AnimationMixer(rig.skinned) : null;
  }, [rig]);

  useEffect(() => {
    const m = mixer.current;
    if (!m || !rig) return;
    m.stopAllAction();
    if (!clip) { rig.skeleton.pose(); return; }
    const c = rig.clips.find((x) => x.name === clip);
    if (!c) return;
    const action = m.clipAction(c);
    // 미리보기는 전부 반복. 단 '마지막 프레임에서 멈춤' 클립(사망·T포즈)은 한 번만 돌고 그 자세로 선다.
    const meta = CLIP_META[clip];
    if (meta?.clamp) { action.reset().setLoop(THREE.LoopOnce, 1); action.clampWhenFinished = true; }
    else action.reset().setLoop(THREE.LoopRepeat, Infinity);
    action.fadeIn(0.15).play();
  }, [rig, clip]);

  useFrame((_, delta) => {
    const d = Math.min(delta, 0.1);
    // 탭이 숨겨졌다 돌아오면 delta 가 수십 초로 튄다. 한 프레임에 0.1초 이상은 안 돌린다.
    if (autoRotate && group.current) group.current.rotation.y += d * 0.35;
    mixer.current?.update(d);
  });

  return (
    <group ref={group}>
      <primitive object={object} />
    </group>
  );
}

function Studio() {
  const environment = useStudioEnvironment();
  const showEnvironment = useViewStore((s) => s.showEnvironment);
  const exposure = useViewStore((s) => s.exposure);
  const scene = useThree((s) => s.scene);
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    scene.environment = environment;
    scene.background = showEnvironment && environment ? environment : new THREE.Color(0x06070a);
    if (environment) scene.backgroundBlurriness = 0.75;
    scene.backgroundIntensity = 0.55;
  }, [scene, environment, showEnvironment]);

  useEffect(() => {
    // ACES 는 붉은 계열을 주황으로, 채도를 낮게 튼다(실측: 붉은 로봇이 갈색). Neutral 은 원색 보존용.
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = exposure;
  }, [gl, exposure]);

  return (
    <>
      {/* 키 라이트. 섀도맵은 끈다 — 셰이더가 커져 D3D 컴파일이 배로 느려지고,
          바닥 그림자는 ContactShadows 가 따로 그린다. */}
      <directionalLight position={[3.5, 5, 4]} intensity={1.1} />
      <ambientLight intensity={0.15} />
    </>
  );
}

function CameraRig() {
  const mesh = useForgeStore((s) => s.mesh);
  const camera = useThree((s) => s.camera);
  void mesh;

  const size = useThree((s) => s.size);

  useEffect(() => {
    if (!mesh) return;
    const box = mesh.geometry.boundingBox;
    const half = new THREE.Vector3(1, 1, 0.3);
    if (box) box.getSize(half).multiplyScalar(0.5);
    const perspective = camera as THREE.PerspectiveCamera;
    const vfov = ((perspective.fov ?? 38) * Math.PI) / 180;
    // 구가 아니라 상자로 맞춘다. 세로로 긴 캐릭터를 구로 맞추면 화면의 절반이 빈다.
    // 세로 화각만 보면 좁은 뷰포트에서 좌우가 잘리니 가로 화각도 같이 본다.
    const aspect = size.width / Math.max(1, size.height);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * aspect);
    const byHeight = half.y / Math.tan(vfov / 2);
    const byWidth = half.x / Math.tan(hfov / 2);
    const distance = (Math.max(byHeight, byWidth) + half.z) * 1.12;
    camera.position.set(0, 0.08 * half.y, distance);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, [mesh, camera, size.width, size.height]);

  return null;
}

export function Viewer() {
  const workspace = useViewStore(s => s.workspace);
  const status = useForgeStore((s) => s.status);
  const stage = useForgeStore((s) => s.stage);
  const error = useForgeStore((s) => s.error);
  const hasMesh = useForgeStore((s) => Boolean(s.mesh));

  return (
    <div className="stage">
      <Canvas
        frameloop={workspace === 'character' ? 'always' : 'never'}
        dpr={[1, 2]}
        camera={{ position: [0, 0.2, 3.2], fov: 38, near: 0.05, far: 100 }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
          preserveDrawingBuffer: true,
        }}
      >
        <Studio />
        <CameraRig />
        <ForgedObject />
        <ContactShadows
          position={[0, -1.15, 0]}
          opacity={0.5}
          scale={6}
          blur={2.4}
          far={3}
          resolution={512}
          color="#000000"
        />
        <OrbitControls
          makeDefault
          enableDamping
          dampingFactor={0.08}
          minDistance={0.8}
          maxDistance={12}
          target={[0, 0, 0]}
        />
      </Canvas>

      {!hasMesh && status !== 'working' && (
        <div className="empty">
          <h3>이미지를 떨어뜨리면 3D가 됩니다</h3>
          <p>
            PNG 스프라이트라면 알파를 그대로 씁니다. 배경이 단색인 JPG도 알아서 잘라냅니다.
            <br />
            왼쪽 패널에서 샘플을 눌러 바로 확인해 볼 수도 있습니다.
          </p>
        </div>
      )}

      {status === 'working' && (
        <div className="overlay">
          <div className="spinner" />
          <div className="msg">{stage?.label ?? '작업 중'}</div>
          {stage?.detail && <div className="detail">{stage.detail}</div>}
          {typeof stage?.ratio === 'number' && (
            <div className="bar">
              <i style={{ width: `${Math.round(stage.ratio * 100)}%` }} />
            </div>
          )}
        </div>
      )}

      {status === 'error' && error && (
        <div className="overlay">
          <div className="msg" style={{ color: 'var(--err)' }}>실패했습니다</div>
          <div className="detail">{error}</div>
        </div>
      )}
    </div>
  );
}
